import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Box,
  Tabs,
  Tab
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import SearchBar from "../../components/SearchBar";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import { useGetCarAccessoriesQuery, useDeleteCarAccessoryMutation, useUpdateCarAccessoryMutation } from "../../service/features/api";
import Pagination from "../../components/Panigation";
import moment from "moment";
import DeleteConfirmModal from "../../Modals/DeleteConfirmModal"; // Ensure correct path
import { usePermission } from "../../hooks/usePermission";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import { VEHICLE_TYPE } from "../../constants/enum";
import { getVehicleTypeLabel } from "../../utils/vehicle";
import { SaudiRiyal } from "lucide-react";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const typeMapping = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
];

const ManageCarAccessories = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Listings Management", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [valueTabs, setValueTabs] = useState(0);

  const tabTypes = typeMapping.map(type => getVehicleTypeLabel(type));

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };

  const { data: accessoryData, isLoading, refetch } = useGetCarAccessoriesQuery({
    search: searchTerm,
    page: page,
    limit: 10,
    type: typeMapping[valueTabs]
  });

  const [deleteCarAccessory, { isLoading: isDeleting }] = useDeleteCarAccessoryMutation();
  const [updateCarAccessory] = useUpdateCarAccessoryMutation();

  const handleToggle = async (row: any, field: string, isChecked: boolean) => {
    // console.log(row?._id, "√ioiiooiiooiooiio");
    try {
      // Helper to safely get ID from object or string
      const getId = (item: any) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && item._id) return item._id;
        return "";
      };
      const payload: any = {
        name: row.name || "",
        categoryId: getId(row.category) || getId(row.categoryId) || "",
        subCategoryId: getId(row.subCategory) || getId(row.subCategoryId) || "",
        brand: getId(row.brand) || "",
        model: row?._id || "",
        condition: Number(getId(row.condition)) || 1,
        dealer: "6952280fef439ea39cf0a6d1", // Hardcoded static ID as requested
        actualPrice: Number(row.actualPrice) || 0,
        discountPrice: Number(row.discountPrice) || 0,
        images: row.images || [],
        shortDescription: row.shortDescription || "",
        fullDescription: row.fullDescription || "",
        isFeatured: row.isFeatured,
        isActive: row.isActive,
        manufacturingYear: row.manufacturingYear || new Date().getFullYear(),
        type: Number(Array.isArray(row.type) ? row.type[0] : (row.type || 1))
      };

      // Set the specific field to the new checked value
      payload[field] = isChecked;

      // Filter out any undefined/null values just in case, though defaults above should handle it
      // However, validation error "received undefined" suggests keys are present but values are undefined.
      // The defaults above ensure they are at least empty strings or 0.

      const res = await updateCarAccessory({ id: row._id, ...payload }).unwrap();
      toast.success(res?.message || "Status updated successfully");
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update status");
      console.error(error);
    }
  };

  const rows = accessoryData?.data?.data || [];
  const paginator = accessoryData?.data?.paginator;

  const handleDeleteClick = (id: string) => {
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await deleteCarAccessory(deleteId).unwrap();
      toast.success(res?.message || "Car Accessory deleted successfully");
      setDeleteModalOpen(false);
      setDeleteId(null);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to delete accessory");
    }
  };

  return (
    <>
      <CommonBreadcrumb
        title="الملحقات المدرجة / Listed Accessories"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "الملحقات المدرجة / Listed Accessories" },
        ]}
      />

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              className="custom_tabs_links"
            >
              {tabTypes.map((label, index) => (
                <Tab key={label} label={label} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="بحث عن ملحق السيارة... / Search car accessory..."
            />
          </div>
          <div className="right_s">
            <Can module="Listings Management" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-car-accessories/add", { state: { defaultType: typeMapping[valueTabs] } })}
              >
                <AddIcon /> إضافة ملحق جديد / Add New Accessory
              </Button>
            </Can>
          </div>
        </div>


        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            <TableContainer className="table_container">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>تفاصيل الملحق / Accessory Detail</TableCell>
                    <TableCell>تفاصيل الوكيل/البائع / Dealer/Seller Detail</TableCell>
                    <TableCell>السعر / Price</TableCell>
                    <TableCell>تاريخ الإنشاء / Created Date</TableCell>
                    <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                    <TableCell>مميز / Featured</TableCell>
                    <TableCell>إجراءات / Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        لا توجد بيانات / No data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row: any) => (
                      <TableRow hover key={row._id}>
                        {/* Car Accessory Detail */}
                        <TableCell>
                          <div className="user_block">
                            <figure className="no_radius">
                              <img
                                src={row.images?.[0] || "/static/images/placeholder.png"}
                                alt={row.name}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = "/static/images/placeholder.png";
                                }}
                              />
                            </figure>
                            <div>
                              <p>
                                <b>{row.name}</b>
                              </p>
                            </div>
                          </div>
                        </TableCell>

                        {/* Dealer Detail */}
                        <TableCell>
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              // navigate("/manage-dealers/detail");
                            }}
                            className="user_block click_action"
                            style={{ cursor: "pointer" }}
                          >
                            <p>
                              <b>{row.dealer?.fullName || row.dealer?.name || "N/A"}</b>
                              <span>{row.dealer?.countryCode || ""}{row.dealer?.phone || "N/A"}</span>
                            </p>
                          </div>
                        </TableCell>

                        {/* Price */}
                        <TableCell>
                          <strong className="c_primary" style={{display: 'flex', alignItems: 'center', gap: '5px'}}><SaudiRiyal size={14} />{row.discountPrice}</strong>{" "}
                          <del><SaudiRiyal size={14} />{row.actualPrice}</del>
                        </TableCell>

                        <TableCell>{moment(row.createdAt).format("DD-MMM-YYYY")}</TableCell>

                        <TableCell>
                          {canEdit ? (
                            <SwitchToggle
                              checked={row.isActive}
                              onChange={(e) => handleToggle(row, 'isActive', e.target.checked)}
                            />
                          ) : (
                            <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                              {row.isActive ? "Enabled" : "Disabled"}
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          {canEdit ? (
                            <SwitchToggle
                              checked={row.isFeatured}
                              onChange={(e) => handleToggle(row, 'isFeatured', e.target.checked)}
                            />
                          ) : (
                            <span className={`status_btn ${row.isFeatured ? "c_dark_info" : "c_dark_warning"}`}>
                              {row.isFeatured ? "Featured" : "Normal"}
                            </span>
                          )}
                        </TableCell>

                        <TableCell>
                          <div className="table_actions v2">
                            <Can module="Listings Management" action="isView">
                              <Tooltip title="View" arrow>
                                <IconButton
                                  onClick={() => navigate(`/manage-car-accessories/detail/${row._id}`, { state: { id: row._id } })}
                                >
                                  <img src="/static/images/eye_icon.svg" alt="View" />
                                </IconButton>
                              </Tooltip>
                            </Can>

                            <Can module="Listings Management" action="isEdit" isDisabledMode={true}>
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  onClick={() => navigate(`/manage-car-accessories/edit/${row._id}`, { state: { id: row._id } })}
                                >
                                  <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                </IconButton>
                              </Tooltip>
                            </Can>

                            <Can module="Listings Management" action="isDelete" isDisabledMode={true}>
                              <Tooltip title="Delete" arrow>
                                <IconButton onClick={() => handleDeleteClick(row._id)}>
                                  <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                </IconButton>
                              </Tooltip>
                            </Can>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {paginator && paginator.itemCount > 10 && (
              <Pagination
                module={rows}
                page={page}
                setPage={setPage}
                totalCount={paginator.itemCount}
                totalPages={paginator.pageCount}
                onPageChange={(newPage) => setPage(newPage)}
              />
            )}
          </CustomTabPanel>
        ))}
      </div>

      <DeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Car Accessory"
        message="Are you sure you want to delete this accessory? This action cannot be undone."
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageCarAccessories;